"use client";

import { useEffect, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Stat = {
  value: number;
  suffix: string;
  prefix: string;
  label: string;
  description: string;
};
const stats: Stat[] = [{
  value: 35,
  suffix: "%",
  prefix: "",
  label: "Revenue Growth",
  description: "Average increase in RevPAR for our partner hotels."
}, {
  value: 12,
  suffix: "M+",
  prefix: "$",
  label: "Revenue Managed",
  description: "Total annual room revenue under our strategic oversight."
}, {
  value: 50,
  suffix: "+",
  prefix: "",
  label: "Hotels Optimized",
  description: "Properties transformed through data-driven strategies."
}, {
  value: 24,
  suffix: "/7",
  prefix: "",
  label: "Support Coverage",
  description: "Continuous monitoring and revenue management support."
}];
function CountUp({
  end,
  isVisible,
  duration = 1800
}: {
  end: number;
  isVisible: boolean;
  duration?: number;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let startTime: number | null = null;
    let rafId: number;
    const tick = (timestamp: number) => {
      if (startTime === null) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(eased * end));
      if (progress < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        setCount(end);
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [end, isVisible, duration]);
  return <>{count.toLocaleString()}</>;
}
export default function Performance() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.3
  });
  return <section data-section-id="4518" id="performancemetricsdashboard" className="bg-background text-foreground py-24 md:py-40 px-6 md:px-12 relative overflow-hidden">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.03]">
        <svg viewBox="0 0 800 800" className="w-[140%] max-w-none" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" strokeWidth="0.5">
          <circle cx="400" cy="400" r="380" />
          <circle cx="400" cy="400" r="320" />
          <circle cx="400" cy="400" r="260" />
          <circle cx="400" cy="400" r="200" />
          <circle cx="400" cy="400" r="140" />
          <circle cx="400" cy="400" r="80" />
          <circle cx="250" cy="400" r="60" />
          <circle cx="550" cy="400" r="60" />
          <circle cx="400" cy="250" r="60" />
          <circle cx="400" cy="550" r="60" />
          <line x1="20" y1="400" x2="780" y2="400" />
          <line x1="400" y1="20" x2="400" y2="780" />
          <line x1="115" y1="115" x2="685" y2="685" />
          <line x1="685" y1="115" x2="115" y2="685" />
          {Array.from({
          length: 60
        }).map((_, i) => {
          const angle = i * 6 * Math.PI / 180;
          const x1 = 400 + Math.cos(angle) * 370;
          const y1 = 400 + Math.sin(angle) * 370;
          const x2 = 400 + Math.cos(angle) * 380;
          const y2 = 400 + Math.sin(angle) * 380;
          return <line key={i} data-index={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
        })}
        </svg>
      </div>

      <div ref={ref} className="relative max-w-7xl mx-auto">
        {}
        <div className="mb-16 md:mb-24 max-w-3xl">
          <div className="flex items-center gap-3 mb-8">
            <span className="font-default text-xs uppercase tracking-widest" style={{
            color: "var(--secondary)"
          }}>
              Proven Results
            </span>
            <span className="font-default text-xs tracking-widest text-muted-foreground">
              / 03
            </span>
          </div>
          <h2 className="font-default italic text-3xl md:text-5xl leading-tight text-foreground" style={{
          letterSpacing: "-0.02em"
        }}>
            Data-driven revenue strategies that deliver measurable growth.
          </h2>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-4 relative">
          {stats.map((stat, i) => <div data-index={i} key={i} className={["relative px-0 md:px-8 py-10 md:py-0", i !== 0 ? "border-t border-border md:border-t-0" : "", i !== 0 ? "md:before:content-[''] md:before:absolute md:before:left-0 md:before:top-[10%] md:before:h-[80%] md:before:w-px md:before:bg-border" : ""].join(" ")}>
              <div className="flex flex-col">
                {}
                <div className="flex items-baseline flex-wrap">
                  <span className="font-default font-bold text-foreground" style={{
                fontSize: "clamp(5rem, 10vw, 9rem)",
                letterSpacing: "-0.06em",
                lineHeight: 0.9
              }}>
                    {stat.prefix}
                    <CountUp end={stat.value} isVisible={isVisible} />
                  </span>
                  {stat.suffix && <span className="font-default text-xl md:text-2xl text-muted-foreground ml-1" style={{
                letterSpacing: "0"
              }}>
                      {stat.suffix}
                    </span>}
                </div>

                {}
                <div className="mt-4 mb-6 h-px w-full bg-border/50 overflow-hidden">
                  <div className="h-full" style={{
                backgroundColor: "var(--secondary)",
                width: isVisible ? "100%" : "0%",
                transition: "width 1800ms cubic-bezier(0.22, 1, 0.36, 1)"
              }} />
                </div>

                {}
                <div className="font-default text-xs uppercase tracking-widest text-foreground mb-3 font-medium" style={{
              letterSpacing: "0.15em"
            }}>
                  {stat.label}
                </div>

                {}
                <p className="font-default text-sm md:text-base text-muted-foreground leading-relaxed max-w-xs">
                  {stat.description}
                </p>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}